/**
 * Redux Slice pour la gestion des profils de détection d'entrées
 * 
 * Gère l'état des profils de détection Anagogia, incluant :
 * - Profil actif
 * - Profils sauvegardés (localement et dans le backend)
 * - Chargement/sauvegarde/suppression de profils
 * 
 * NOTE: Ce slice gère les profils de DÉTECTION automatique d'entrées dans le texte,
 * pas les profils utilisateur (voir userProfile pour les préférences utilisateur).
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { detectionProfileService, setAuthToken } from './detectionProfileAPI';
import { Profile, CreateProfileRequest, UpdateProfileRequest } from '../../types/api';
import { LOADING_STATES } from '../../constants';
import type { EntreesMinerProfile } from '../../lib/entree-detection/types/entreesMinerProfile';

interface DetectionProfileState {
  activeProfile: EntreesMinerProfile;
  savedProfiles: EntreesMinerProfile[];
  status: string;
  error: string | null;
}

/**
 * PROFIL PAR DÉFAUT - Fallback système
 * Utilisé uniquement si aucun profil n'est chargé ou sélectionné.
 */
const defaultProfile: EntreesMinerProfile = {
  nom: 'Anagogia - Détection Standard',
  description: 'Profil équilibré pour l\'édition quotidienne de notes et documents',
  userId: 'anagogia-system',
  detectors: {
    exact_word: { enabled: true, autoValidate: true },
    case_insensitive: { enabled: true, autoValidate: true },
    start_with: { enabled: false, autoValidate: false },
    word_start_with: { enabled: true, autoValidate: true },
    contains: { enabled: false, autoValidate: false },
    acronym: { enabled: true, autoValidate: true },
    stemming: { enabled: false, autoValidate: false },
    likelihood: { enabled: false, autoValidate: false, threshold: 0.7 }
  },
  sources: {
    mode: 'all_entrees',
    includeFields: {
      cle: true,
      subEntrees: true,
      label: true
    }
  }
};

const initialState: DetectionProfileState = {
  activeProfile: defaultProfile,
  savedProfiles: [], // Uniquement les profils utilisateur chargés depuis l'API
  status: LOADING_STATES.IDLE,
  error: null
};

/**
 * Charge les profils de détection de l'utilisateur depuis le backend
 */
export const fetchDetectionProfiles = createAsyncThunk(
  'detectionProfile/fetchDetectionProfiles',
  async (accessToken: string, { rejectWithValue }) => {
    try {
      setAuthToken(accessToken);
      const profiles = await detectionProfileService.getAll();
      console.log('📥 Redux fetchDetectionProfiles result:', profiles);
      return profiles as unknown as EntreesMinerProfile[];
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement des profils de détection:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Sauvegarde un profil de détection (dans le backend)
 */
export const saveDetectionProfile = createAsyncThunk(
  'detectionProfile/saveDetectionProfile',
  async ({ profile, accessToken }: { profile: CreateProfileRequest; accessToken: string }, { rejectWithValue }) => {
    try {
      setAuthToken(accessToken);
      const saved = await detectionProfileService.create(profile);
      return saved as unknown as EntreesMinerProfile;
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde du profil de détection:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Met à jour un profil de détection (dans le backend)
 */
export const updateDetectionProfile = createAsyncThunk(
  'detectionProfile/updateDetectionProfile',
  async ({ id, data, accessToken }: { id: string; data: UpdateProfileRequest; accessToken: string }, { rejectWithValue }) => {
    try {
      setAuthToken(accessToken);
      const updated = await detectionProfileService.update(id, data);
      return updated as unknown as EntreesMinerProfile;
    } catch (error: any) {
      console.error('❌ Erreur lors de la mise à jour du profil de détection:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Supprime un profil de détection (dans le backend)
 */
export const deleteDetectionProfile = createAsyncThunk(
  'detectionProfile/deleteDetectionProfile',
  async ({ id, accessToken }: { id: string; accessToken: string }, { rejectWithValue }) => {
    try {
      setAuthToken(accessToken);
      await detectionProfileService.delete(id);
      return id;
    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression du profil de détection:', error);
      return rejectWithValue(error.message);
    }
  }
);

const detectionProfileSlice = createSlice({
  name: 'detectionProfile',
  initialState,
  reducers: {
    /**
     * Change le profil de détection actif
     */
    setActiveDetectionProfile: (state, action: PayloadAction<EntreesMinerProfile>) => {
      state.activeProfile = action.payload;
      console.log(`✅ Profil de détection actif : "${action.payload.nom}"`);
    },

    /**
     * Réinitialise aux profils par défaut
     */
    resetToDefaultDetectionProfile: (state) => {
      state.activeProfile = defaultProfile;
      console.log('✅ Profil de détection réinitialisé à la valeur par défaut');
    },

    /**
     * Nettoie les erreurs
     */
    clearDetectionProfileError: (state) => {
      state.error = null;
    }
  },

  extraReducers: (builder) => {
    // Fetch detection profiles
    builder
      .addCase(fetchDetectionProfiles.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(fetchDetectionProfiles.fulfilled, (state, action) => {
        state.status = LOADING_STATES.SUCCEEDED;
        state.savedProfiles = action.payload;
      })
      .addCase(fetchDetectionProfiles.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.payload as string;
      });

    // Save detection profile
    builder
      .addCase(saveDetectionProfile.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(saveDetectionProfile.fulfilled, (state, action) => {
        state.status = LOADING_STATES.SUCCEEDED;
        state.savedProfiles.push(action.payload);
        state.activeProfile = action.payload;
      })
      .addCase(saveDetectionProfile.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.payload as string;
      });

    // Update detection profile
    builder
      .addCase(updateDetectionProfile.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(updateDetectionProfile.fulfilled, (state, action) => {
        state.status = LOADING_STATES.SUCCEEDED;
        const idx = state.savedProfiles.findIndex(p => p._id === action.payload._id);
        if (idx > -1) {
          state.savedProfiles[idx] = action.payload;
        }
        if (state.activeProfile._id === action.payload._id) {
          state.activeProfile = action.payload;
        }
      })
      .addCase(updateDetectionProfile.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.payload as string;
      });

    // Delete detection profile
    builder
      .addCase(deleteDetectionProfile.pending, (state) => {
        state.status = LOADING_STATES.LOADING;
      })
      .addCase(deleteDetectionProfile.fulfilled, (state, action) => {
        state.status = LOADING_STATES.SUCCEEDED;
        state.savedProfiles = state.savedProfiles.filter(
          p => p._id !== action.payload
        );
        
        // Si le profil actif est supprimé, revenir au profil par défaut
        if (state.activeProfile._id === action.payload) {
          state.activeProfile = defaultProfile;
        }
      })
      .addCase(deleteDetectionProfile.rejected, (state, action) => {
        state.status = LOADING_STATES.FAILED;
        state.error = action.payload as string;
      });
  }
});


// Actions
export const {
  setActiveDetectionProfile,
  resetToDefaultDetectionProfile,
  clearDetectionProfileError
} = detectionProfileSlice.actions;

// Selectors
export const selectActiveDetectionProfile = (state: RootState) => state.detectionProfile.activeProfile;
export const selectSavedDetectionProfiles = (state: RootState) => state.detectionProfile.savedProfiles;
export const selectDetectionProfileStatus = (state: RootState) => state.detectionProfile.status;
export const selectDetectionProfileError = (state: RootState) => state.detectionProfile.error;

export const selectDetectionProfileByName = (nom: string) => (state: RootState) =>
  state.detectionProfile.savedProfiles.find(p => p.nom === nom);

// Backward compatibility aliases (deprecated - will be removed in future)
export const fetchUserProfiles = fetchDetectionProfiles;
export const saveProfile = saveDetectionProfile;
export const updateProfile = updateDetectionProfile;
export const deleteProfile = deleteDetectionProfile;
export const setActiveProfile = setActiveDetectionProfile;
export const resetToDefaultProfiles = resetToDefaultDetectionProfile;
export const clearError = clearDetectionProfileError;
export const selectActiveProfile = selectActiveDetectionProfile;
export const selectSavedProfiles = selectSavedDetectionProfiles;
export const selectProfileStatus = selectDetectionProfileStatus;
export const selectProfileError = selectDetectionProfileError;
export const selectProfileByName = selectDetectionProfileByName;

export default detectionProfileSlice.reducer;
